'use client';

/**
 * NotificationCenter Component
 *
 * Main notification dropdown component that replaces the mock notifications
 * in AppHeader. Displays notification list with filtering and actions.
 *
 * ENRICHED popup (std-overlays G14): the panel content (live list, filters, actions) is its own; the shell is the
 * shared Popover (popup surface, layer stack: Escape and an outside press close the top layer only; focus returns to
 * the bell after Escape or a choice inside the panel, not after an outside press).
 */

import React, { useRef, useCallback } from 'react';
import Link from 'next/link';
import { Bell, X, Check, Settings, Loader2, RefreshCw } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectNotifications,
  selectActionableUnreadCount,
  selectIsDropdownOpen,
  selectIsLoading,
  selectActiveFilter,
  selectHasMore,
  selectError,
  setDropdownOpen,
  setActiveFilter,
} from '@/store/slices/notificationsSlice';
import { useNotifications } from '@/hooks/useNotifications';
import type { Notification, NotificationCategory } from '@/store/slices/notificationsTypes';
import { NOTIFICATION_CATEGORIES } from '@/store/slices/notificationsTypes';
import { NotificationItem } from './NotificationItem';
import { NotificationBadge, NotificationDot } from './NotificationBadge';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface NotificationCenterProps {
  /** Compact mode for smaller headers (e.g., conference) */
  compact?: boolean;
  /** Custom class name */
  className?: string;
  /** Callback when notification is clicked */
  onNotificationClick?: (notification: Notification) => void;
}

export function NotificationCenter({
  compact = false,
  className,
  onNotificationClick,
}: NotificationCenterProps) {
  const dispatch = useAppDispatch();
  const bellRef = useRef<HTMLButtonElement>(null);

  // Selectors
  const notifications = useAppSelector(selectNotifications);
  const actionableUnreadCount = useAppSelector(selectActionableUnreadCount);
  const isOpen = useAppSelector(selectIsDropdownOpen);
  const isLoading = useAppSelector(selectIsLoading);
  const activeFilter = useAppSelector(selectActiveFilter);
  const hasMore = useAppSelector(selectHasMore);
  const error = useAppSelector(selectError);

  // Hook for refresh functionality
  const {
    refresh,
    markAsRead: persistMarkAsRead,
    markAllAsRead: persistMarkAllAsRead,
    archive: persistArchive,
  } = useNotifications({ autoFetch: false });

  // Filter notifications based on active filter
  const filteredNotifications = activeFilter === 'all'
    ? notifications
    : notifications.filter((n) => n.category === activeFilter);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      dispatch(setDropdownOpen(open));
    },
    [dispatch]
  );

  // A choice inside the panel (Close, a link, a notification) closes it and hands focus back to the bell (2.4.3).
  const handleClose = useCallback(() => {
    dispatch(setDropdownOpen(false));
    bellRef.current?.focus();
  }, [dispatch]);

  const handleArchive = useCallback(
    (id: string) => {
      void persistArchive(id);
    },
    [persistArchive]
  );

  const handleMarkAllRead = useCallback(() => {
    void persistMarkAllAsRead();
  }, [persistMarkAllAsRead]);

  const handleFilterChange = useCallback(
    (filter: NotificationCategory | 'all') => {
      dispatch(setActiveFilter(filter));
    },
    [dispatch]
  );

  const handleNotificationClick = useCallback(
    (notification: Notification) => {
      if (!notification.isRead) {
        void persistMarkAsRead(notification.id);
      }
      if (onNotificationClick) {
        onNotificationClick(notification);
      }
      // Open the notification's target page in a new window/tab
      if (notification.actionUrl) {
        window.open(notification.actionUrl, '_blank', 'noopener');
      }
      handleClose();
    },
    [persistMarkAsRead, onNotificationClick, handleClose]
  );

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
    <div className={cn('relative', className)}>
      {/* Bell Button */}
      <PopoverTrigger asChild>
      <button
        ref={bellRef}
        className={cn(
          'p-2 rounded-lg text-white/70 hover:text-white',
          'hover:bg-white/[0.06] transition-colors relative group'
        )}
        aria-label={`Notifications${actionableUnreadCount > 0 ? ` (${actionableUnreadCount} unread)` : ''}`}
      >
        <div
          className={cn(
            'p-2 rounded-lg transition-all duration-200',
            'bg-amber-500/10 group-hover:scale-105'
          )}
        >
          <Bell className="w-5 h-5 text-amber-400" />
        </div>

        {/* Badge - shows count when there are actionable unread notifications */}
        {actionableUnreadCount > 0 && (
          <NotificationBadge
            count={actionableUnreadCount}
            size="sm"
            className="absolute top-0 right-0"
            animate={true}
          />
        )}
      </button>
      </PopoverTrigger>
    </div>

      {/* Dropdown Panel - the shared popup shell; the same width regardless of compact mode */}
      <PopoverContent
        align="end"
        width="lg"
        aria-label="Notifications"
        data-notification-panel=""
        className="w-[28rem] p-0"
      >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-edge">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-400" />
              <span className="text-sm font-semibold text-white">
                Notifications
              </span>
              {actionableUnreadCount > 0 && (
                <NotificationBadge count={actionableUnreadCount} size="sm" />
              )}
            </div>

            <div className="flex items-center gap-1">
              {/* Mark all read button */}
              {actionableUnreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className={cn(
                    'p-1.5 rounded text-white/70 hover:text-white',
                    'hover:bg-white/[0.06] transition-colors'
                  )}
                  title="Mark all as read"
                >
                  <Check className="w-4 h-4" />
                </button>
              )}

              {/* Settings link */}
              <Link
                href="/settings/ui/notifications"
                className={cn(
                  'p-1.5 rounded text-white/70 hover:text-white',
                  'hover:bg-white/[0.06] transition-colors'
                )}
                title="Notification settings"
                onClick={handleClose}
              >
                <Settings className="w-4 h-4" />
              </Link>

              {/* Close button */}
              <button aria-label="Close notifications"
                onClick={handleClose}
                className={cn(
                  'p-1 rounded text-white/70 hover:text-white',
                  'hover:bg-white/[0.06] transition-colors'
                )}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="px-4 py-2 text-sm bg-red-500/10 border-b border-red-500/20 flex items-center justify-between">
              <span className="text-red-400 truncate flex-1">{error}</span>
              <button
                onClick={refresh}
                className="ml-2 p-1 rounded text-red-300 hover:text-red-200 hover:bg-red-500/20 transition-colors flex items-center gap-1"
                title="Retry"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span className="text-xs">Retry</span>
              </button>
            </div>
          )}

          {/* Filter Tabs - Always shown for consistency */}
          <div className="flex items-center gap-1 px-3 py-2 border-b border-edge overflow-x-auto">
            <FilterTab
              label="All"
              isActive={activeFilter === 'all'}
              onClick={() => handleFilterChange('all')}
            />
            {NOTIFICATION_CATEGORIES.slice(0, 5).map((cat) => (
              <FilterTab
                key={cat.id}
                label={cat.label}
                isActive={activeFilter === cat.id}
                onClick={() => handleFilterChange(cat.id)}
              />
            ))}
          </div>

          {/* Notification List - Consistent height */}
          <div className="overflow-y-auto max-h-[min(24rem,calc(100dvh-12rem))]">
            {isLoading && filteredNotifications.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-white/60 animate-spin" />
              </div>
            ) : filteredNotifications.length === 0 ? (
              <EmptyState filter={activeFilter} />
            ) : (
              filteredNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onArchive={handleArchive}
                  onClick={handleNotificationClick}
                />
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-edge">
            <Link
              href="/notifications"
              target="_blank"
              rel="noopener"
              className={cn(
                'block w-full text-center text-sm text-amber-400',
                'hover:text-amber-300 transition-colors'
              )}
              onClick={handleClose}
            >
              View All Notifications
            </Link>
          </div>
      </PopoverContent>
    </Popover>
  );
}

// Filter tab component
function FilterTab({
  label,
  isActive,
  onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button aria-pressed={isActive}
      onClick={onClick}
      className={cn(
        'px-2.5 py-1 text-xs font-medium rounded-md whitespace-nowrap',
        'transition-colors',
        isActive
          ? 'bg-amber-500/20 text-amber-400'
          : 'text-white/70 hover:text-white hover:bg-white/[0.06]'
      )}
    >
      {label}
    </button>
  );
}

// Empty state component
function EmptyState({ filter }: { filter: NotificationCategory | 'all' }) {
  const categoryMeta = filter !== 'all'
    ? NOTIFICATION_CATEGORIES.find((c) => c.id === filter)
    : null;

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="p-4 rounded-full bg-white/[0.04] mb-3">
        <Bell className="w-8 h-8 text-slate-500" />
      </div>
      <p className="text-sm text-white/70">
        {filter === 'all'
          ? 'No notifications yet'
          : `No ${categoryMeta?.label.toLowerCase() || filter} notifications`}
      </p>
      <p className="text-xs text-white/60 mt-1">
        New notifications will appear here
      </p>
    </div>
  );
}

export default NotificationCenter;
